// F138e: what a partner may do WHILE looking through the client's eyes.
//
// Pavel asked for actions on the client's behalf ("abych tam klidně i za něj
// mohl dělat akce"). The audit's condition for turning them on was that the
// list is an ENUMERATION OF WHAT IS ALLOWED, never a list of what is banned:
// a route added tomorrow must be closed by default, or the next feature
// silently widens what a partner can do in somebody else's name.
//
// ONE definition, two languages: the Worker enforces it (security.ts) and the
// browser gates its own fetch with it (src/readonly.ts), so the screen can
// never offer what the server would refuse, and the refusal the partner reads
// is the refusal the server would have given.

export type ImpersonationWriteAllowance =
  /** Session management: ending the preview must always be possible. */
  | "session"
  /** Answering the questionnaire for a client who is stuck on it. */
  | "questionnaire"
  /** Ticking a step the client has really done. */
  | "step"
  /** Notes and podnety: the client's own words, written down for them. */
  | "note"
  /** Uploading a file the client sent by another route. */
  | "upload"
  /**
   * F150. Watching the client's video, which is what MOVES their step: the
   * intro and the marketing video ARE steps (F81), and the seconds the player
   * reports are what fire S02/S11 at the configured unlock moment.
   *
   * Pavel, 2026-08-14: "koukal jsem na to jako administrator, kterej se
   * prepnul na pohled klienta [...] melo by se to chovat stejne, jako kdyz je
   * to samotnej klient." Left off this list the progress POST was refused as
   * `other`, so the stored seconds never grew, the threshold never arrived and
   * the unlock band never appeared - under a player that looked perfectly
   * healthy. Measured on dev: NULL rows in `video_progress` for both clients
   * tested through "Otevrit jako klient".
   *
   * It belongs here by the list's own logic: a partner may already submit the
   * questionnaire and tick a step for a client, both bigger acts than watching
   * a recording, and both of which this one merely opens the door to.
   */
  | "video"
  /**
   * Dictation. Every voice surface the client has - the questionnaire answer,
   * the podnet, the walk comment over a document - records and then asks this
   * route for the text. The write it feeds is already allowed above, so
   * leaving transcription closed made those allowances unreachable by voice
   * and reported it as a failed transcript.
   */
  | "voice"
  /**
   * Act-approve (Pavel 2026-08-19): the CLIENT's own material approvals.
   *
   * 🔴 This REVERSES a written F138e refusal. `approval` used to be a NAMED
   * refusal ("An approval that already has its own staff home, the F104
   * override panel"), and Pavel overruled it from the surface itself: as an
   * administrator in "Otevřít jako klient" with acting ON he approved the
   * strategy for the client and was refused. "Když kliknu, že za ní chci
   * jednat, tak by mi to mělo povolit." Acting for the client includes making
   * the client's own approvals; the F104 override stays what it always was -
   * a reversal with a written reason - while this is the client's own path,
   * executed by the partner, attributed by the `impersonated_write` audit row.
   *
   * ONLY the client approve route. The named-closed acts (handover, publish,
   * payments) stay closed: an approval is revisable by F104, those are not.
   */
  | "approval";

/**
 * Why a write stays closed. The bar prints the matching sentence, so every
 * refusal names ITSELF instead of a generic "not allowed" (F44).
 */
export type ImpersonationWriteRefusal =
  /** The bot, the instance and the owner role really change hands. */
  | "handover"
  /** Publishing on the client's own Netlify account. */
  | "publish"
  /** Anything around money. */
  | "payments"
  // `approval` WAS a named refusal here ("its own staff home, the F104
  // override panel") until Pavel reversed it from the surface (act-approve,
  // 2026-08-19). It is an allowance now - see ImpersonationWriteAllowance.
  /**
   * Not a closed act at all: the write IS on the allowance list, but the
   * session is still in "jen koukám". The bar answers it with the switch,
   * never with an apology. Only the mode check produces this one.
   */
  | "looking"
  /** Deny by default: not on the list. */
  | "other";

export type ImpersonationWriteDecision =
  | { allow: true; allowance: ImpersonationWriteAllowance }
  | { allow: false; refusal: ImpersonationWriteRefusal };

type Method = "GET" | "HEAD" | "OPTIONS" | "POST" | "PUT" | "PATCH" | "DELETE" | string;

interface Rule {
  method: Method;
  /** Anchored, so a longer path can never match a shorter rule. */
  pattern: RegExp;
}

const SEGMENT = "[^/]+";

function rule(method: Method, path: string): Rule {
  // `:name` in a path stands for exactly one segment, like the Hono routes.
  const source = path
    .split("/")
    .map((part) => (part.startsWith(":") ? SEGMENT : part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")))
    .join("/");
  return { method, pattern: new RegExp(`^${source}$`) };
}

/**
 * Always open: without these the partner could neither end the preview nor
 * take the deliberate step from "jen koukám" to "můžu jednat".
 */
const SESSION_RULES: Rule[] = [
  rule("POST", "/api/auth/impersonation/end"),
  rule("POST", "/api/auth/impersonation/mode"),
  rule("POST", "/api/auth/logout")
];

const ALLOWED: Array<{ allowance: ImpersonationWriteAllowance; rules: Rule[] }> = [
  {
    allowance: "questionnaire",
    rules: [
      rule("PUT", "/api/questionnaires/:questionnaire/answers/:itemKey"),
      rule("POST", "/api/questionnaires/:questionnaire/submit")
    ]
  },
  {
    // 🔴 `dashboard` is deliberately NOT here, see NAMED_REFUSALS: that
    // confirmation CLOSES the handover (F134), and F104 lists handover_close
    // as a state that does not come back.
    allowance: "step",
    rules: [rule("POST", "/api/client-steps/:step/confirm")]
  },
  {
    allowance: "note",
    rules: [
      rule("POST", "/api/feedback"),
      rule("POST", "/api/profile/feedback"),
      rule("POST", "/api/deliverables/:deliverableId/feedback")
    ]
  },
  {
    allowance: "upload",
    rules: [rule("POST", "/api/attachments")]
  },
  {
    // 🔴 The F150 shape, one surface over: a partner acting for a client may
    // already leave a note (`note`), answer a question (`questionnaire`) and
    // store the recording (`upload`) - and every one of those is DICTATED
    // through this route, which was closed. So the whole voice half of the
    // product was unreachable in acting mode, and the recorder reported it as
    // "the transcript did not finish, send it again": a paid retry that could
    // never work. Transcription buys text for a write already allowed; it
    // decides nothing and stores nothing of its own.
    allowance: "voice",
    rules: [rule("POST", "/api/transcribe-live"), rule("POST", "/api/transcribe-realtime-token")]
  },
  {
    // F150: ONLY the progress report. `GET /api/video/:videoKey` is a read and
    // passes on the safe-method rule; nothing else on this path is a write the
    // client's own player can make.
    allowance: "video",
    rules: [rule("POST", "/api/video/:videoKey/progress")]
  },
  {
    // Act-approve: the ONE route a client's own approval click ever hits. The
    // strategy, the sales page, the e-mails and the webinar all approve here
    // (audit+plan fold into one strategy decision inside the route itself).
    // Enumerated by the exact pattern, never a prefix: the sibling
    // /feedback POST is already `note`, and anything else under
    // /api/deliverables stays deny-by-default.
    allowance: "approval",
    rules: [rule("POST", "/api/deliverables/:deliverableId/approve")]
  }
];

/**
 * Acts that stay closed and say WHY. Everything not listed here is closed too
 * (the `other` default); these get their own sentence because the partner will
 * genuinely reach for them and deserves to know whose move it is.
 */
const NAMED_REFUSALS: Array<{ refusal: ImpersonationWriteRefusal; rules: Rule[] }> = [
  {
    refusal: "handover",
    rules: [
      // F118/F128: the stored Telegram id transfers the instance and
      // re-encrypts every API key under a new owner.
      rule("POST", "/api/delivery/handover-telegram"),
      // F113: the bot really changes owner inside Telegram.
      rule("POST", "/api/delivery/bot-accepted"),
      // F134: this confirmation closes the handover, and a closed handover
      // does not reopen (F104).
      rule("POST", "/api/client-steps/dashboard/confirm")
    ]
  },
  {
    refusal: "publish",
    rules: [rule("POST", "/api/delivery/salespage/netlify")]
  },
  {
    refusal: "payments",
    rules: [
      rule("POST", "/api/delivery/stripe-invite-confirmed"),
      rule("PUT", "/api/delivery/order-links"),
      rule("POST", "/api/delivery/order-links/confirm"),
      rule("POST", "/api/delivery/order-links/integrate")
    ]
  }
];

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

function matches(rules: Rule[], method: string, path: string): boolean {
  return rules.some((item) => item.method === method && item.pattern.test(path));
}

/**
 * The one answer. `path` is a pathname without a query string; `method` is
 * upper-cased here so a caller cannot widen the list by casing.
 */
export function impersonationWriteDecision(
  method: string | undefined,
  path: string
): ImpersonationWriteDecision {
  const verb = (method ?? "GET").toUpperCase();
  if (SAFE_METHODS.has(verb)) return { allow: true, allowance: "session" };
  if (matches(SESSION_RULES, verb, path)) return { allow: true, allowance: "session" };

  // A named refusal wins over the allowance list on purpose: it is how
  // `/api/client-steps/dashboard/confirm` is carved out of the step rule.
  for (const group of NAMED_REFUSALS) {
    if (matches(group.rules, verb, path)) return { allow: false, refusal: group.refusal };
  }
  for (const group of ALLOWED) {
    if (matches(group.rules, verb, path)) return { allow: true, allowance: group.allowance };
  }
  return { allow: false, refusal: "other" };
}

/** Locale key of the sentence the floating bar prints for a refusal. */
export function impersonationRefusalKey(refusal: ImpersonationWriteRefusal): string {
  return `impersonation.refusal.${refusal}`;
}

/** Every refusal that has a sentence, so a guard can assert locale parity. */
export const IMPERSONATION_REFUSALS: ImpersonationWriteRefusal[] = [
  "handover",
  "publish",
  "payments",
  "looking",
  "other"
];
